import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Search, UserPlus } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface VincerBaseUser {
  name: string;
  email: string;
}

interface MentorData {
  uuid: string;
  vincer_base_user: VincerBaseUser;
}

interface MenteeData {
  uuid: string;
  vincer_base_user: VincerBaseUser;
}

interface RelationshipAssignment {
  uuid: string;
  start: string;
  end: string | null;
  mentor: MentorData;
  mentee: MenteeData;
}

interface Assignment {
  uuid: string;
  start: string;
  end: string | null;
  mentor: {
    name: string;
    email: string;
  };
  mentee: {
    name: string;
    email: string;
  };
}

export function AssignmentsList() {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchAssignments() {
      try {
        const { data, error: fetchError } = await supabase
          .from('relationship_assignments')
          .select(`
            uuid,
            start,
            end,
            mentor:mentor_uuid (
              uuid,
              vincer_base_user (
                name,
                email
              )
            ),
            mentee:mentee_uuid (
              uuid,
              vincer_base_user (
                name,
                email
              )
            )
          `)
          .order('start', { ascending: false });

        if (fetchError) throw fetchError;

        if (data) {
          const formattedAssignments = (data as unknown as RelationshipAssignment[]).map(assignment => ({
            uuid: assignment.uuid,
            mentor: {
              name: assignment.mentor.vincer_base_user.name,
              email: assignment.mentor.vincer_base_user.email,
            },
            mentee: {
              name: assignment.mentee.vincer_base_user.name,
              email: assignment.mentee.vincer_base_user.email,
            },
            start: assignment.start,
            end: assignment.end,
          }));
          setAssignments(formattedAssignments);
        }
      } catch (err) {
        console.error('Error fetching assignments:', err);
        setError('Failed to load assignments');
      } finally {
        setLoading(false);
      }
    }

    fetchAssignments();
  }, []);

  const filteredAssignments = assignments.filter(assignment => {
    const searchLower = searchTerm.toLowerCase();
    return (
      assignment.mentor.name.toLowerCase().includes(searchLower) ||
      assignment.mentor.email.toLowerCase().includes(searchLower) ||
      assignment.mentee.name.toLowerCase().includes(searchLower) ||
      assignment.mentee.email.toLowerCase().includes(searchLower)
    );
  });

  const getStatus = (assignment: Assignment) => {
    if (assignment.end) return 'completed';
    return 'active';
  };

  const getStatusColor = (status: 'active' | 'completed') => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">Mentorship Assignments</h1>
        <button
          onClick={() => navigate('/coordinator/assignments/new')}
          className="flex items-center gap-2 bg-[#4CAF50] text-white px-4 py-2 rounded-lg hover:bg-[#45a049] transition-colors"
        >
          <UserPlus className="w-5 h-5" />
          <span>New Assignment</span>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border mb-6">
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search assignments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ffcc32] focus:border-transparent"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffcc32] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading assignments...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <p className="text-red-600">{error}</p>
          </div>
        ) : filteredAssignments.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-600">No assignments found</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredAssignments.map((assignment) => {
              const status = getStatus(assignment);
              return (
                <div
                  key={assignment.uuid}
                  className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/coordinator/assignments/${assignment.uuid}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-medium">
                          {assignment.mentor.name}
                        </h3>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                        <h3 className="font-medium">
                          {assignment.mentee.name}
                        </h3>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{assignment.mentor.email}</span>
                        <span>→</span>
                        <span>{assignment.mentee.email}</span>
                      </div>
                      <div className="text-sm text-gray-500">
                        Started: {new Date(assignment.start).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(status)}`}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}